"use client"

import { Menu, Shield } from "lucide-react"
import { useState } from "react"

import { ConnectWalletButton } from "@shared/web3"
import { Button } from "@shared/ui/components/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@shared/ui/components/sheet"
import { cn } from "@shared/ui/lib/utils"

import { LocaleSwitcher } from "@/features/i18n/locale-switcher"
import { useTranslations } from "@/lib/i18n/client"
import { useWalletLabels } from "@/lib/wallet-labels"

const NAV_LINKS = [
  { href: "#scan", key: "scan" as const },
  { href: "#risks", key: "risks" as const },
  { href: "#map", key: "map" as const },
  { href: "#report", key: "report" as const },
] as const

export function SiteHeader() {
  const t = useTranslations("nav")
  const tWallet = useTranslations("wallet")
  const walletLabels = useWalletLabels()
  const [open, setOpen] = useState(false)

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4">
        <a href="#" className="flex min-w-0 items-center gap-2 font-semibold tracking-tight">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Shield className="size-4" />
          </span>
          <span className="truncate text-sm sm:text-base">{t("brand")}</span>
        </a>

        <nav className="hidden items-center gap-6 text-sm text-muted-foreground md:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.key}
              href={link.href}
              className="transition-colors hover:text-foreground"
            >
              {t(link.key)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LocaleSwitcher />
          <div className="hidden items-center gap-2 lg:flex">
            <ConnectWalletButton
              connectLabel={tWallet("connect")}
              disconnectLabel={tWallet("disconnect")}
              networkLabel={tWallet("network")}
              labels={walletLabels}
              size="sm"
            />
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="md:hidden"
                  aria-label={t("menu")}
                />
              }
            >
              <Menu className="size-5" />
            </SheetTrigger>
            <SheetContent side="right" className="border-white/10 bg-background/95">
              <SheetHeader>
                <SheetTitle>{t("menu")}</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-4 px-6">
                {NAV_LINKS.map((link) => (
                  <a
                    key={link.key}
                    href={link.href}
                    onClick={() => setOpen(false)}
                    className={cn("text-sm text-muted-foreground hover:text-foreground")}
                  >
                    {t(link.key)}
                  </a>
                ))}
              </nav>
              <div className="mt-8 flex flex-col gap-3 px-6 pb-6">
                <ConnectWalletButton
                  connectLabel={tWallet("connect")}
                  disconnectLabel={tWallet("disconnect")}
                  networkLabel={tWallet("network")}
                  labels={walletLabels}
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
